import { isPlatformBrowser, NgTemplateOutlet } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  effect,
  inject,
  input,
  output,
  signal,
  TemplateRef,
} from "@angular/core";
import { RouterLink, RouterLinkActive } from "@angular/router";
import { BaseComponent } from "primeng/basecomponent";
import { OverlayBadge } from "primeng/overlaybadge";
import { Tooltip } from "primeng/tooltip";
import { SidenavHeaderComponent } from "../header/sidenav-header.component";
import { SidenavPanelComponent } from "../panel/sidenav-panel.component";
import { SidenavItemStyle } from "./sidenav-item.style";

@Component({
  selector: "ui-sidenav-item",
  templateUrl: "./sidenav-item.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Tooltip, NgTemplateOutlet, OverlayBadge, RouterLink],
  hostDirectives: [RouterLinkActive],
  host: { "[class]": "cx('root')" },
})
export class SidenavItemComponent extends BaseComponent {
  protected readonly _componentStyle = inject(SidenavItemStyle);

  readonly label = input.required<string>();
  readonly icon = input<string>();
  readonly badge = input<string | number>();
  readonly routerLink = input<string>();
  readonly iconOnly = input<boolean>(false);
  /** Greyed out and not clickable, e.g. because the license does not include the feature. */
  readonly locked = input<boolean>(false);
  /** Tooltip of a locked item, explains why it is locked. Defaults to the label. */
  readonly lockedHint = input<string>();

  readonly clicked = output<MouseEvent>();

  private readonly routerLinkActive = inject(RouterLinkActive);
  protected readonly _routerLinkActiveClass = effect(() => {
    this.routerLinkActive.routerLinkActive = this.cx("active");
  });

  protected readonly iconTemplate = contentChild<TemplateRef<void>>("icon");

  private readonly panel = inject(SidenavPanelComponent);
  private readonly header = inject(SidenavHeaderComponent, { optional: true });
  protected readonly active = computed(() => this.header && this.panel.active());

  protected readonly tooltip = computed(() => {
    if (this.locked()) {
      return this.lockedHint() ?? this.label();
    }
    return this.isIconOnly() ? this.label() : undefined;
  });

  protected onClick(event: MouseEvent): void {
    if (this.locked()) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    this.clicked.emit(event);
  }

  laptopBreakpoint = 1280; //Ankaadia.semantic.breakpoint.laptop
  screenWidth = signal(isPlatformBrowser(this.platformId) ? window.innerWidth : this.laptopBreakpoint);
  isIconOnly = computed(() => {
    const explicit = this.iconOnly();
    if (explicit) return explicit;

    return this.screenWidth() < this.laptopBreakpoint;
  });

  constructor() {
    super();
    if (isPlatformBrowser(this.platformId)) {
      effect((onCleanup) => {
        const handler = (): void => this.screenWidth.set(window.innerWidth);
        window.addEventListener("resize", handler);
        onCleanup(() => window.removeEventListener("resize", handler));
      });
    }
  }
}
